import { Editor } from "@monaco-editor/react";
import { Alert, Link } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import * as React from "react";

import { useColorMode } from "@/providers/theme/color";
import { IframeContent } from "@/shared/components/IframeContent";
import { MAX_FILE_SIZE_PREVIEW } from "@/shared/domain/files/FileRecord";
import {
  getMonacoLanguage,
  isHtml,
  isStructuredText,
  isWebRenderable,
} from "@/shared/domain/files/fileTypes";
import { useSignedUrl } from "@/shared/domain/files/hooks/useFile";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";
import { DocumentPanelState, LayoutItem } from "@/shared/state/visualization";

import { SidebarViewType, useWorkspaceSidebar } from "../../sidebarContext";
import {
  useEphemeralWorkspaceStateLoading,
  useSelectPanel,
  useWorkspaceFiles,
} from "../../WorkspaceCtx/hooks";
import { PanelLayout } from "../PanelLayout";

import styles from "./DocumentPanel.module.css";

interface DocumentPanelProps {
  layout: LayoutItem;
  state: DocumentPanelState;
}

/**
 * Derives the Monaco theme from the app's color mode context.
 * Handles the "auto" case by tracking the system preference via matchMedia.
 */
function useMonacoTheme(): string {
  const { colorMode } = useColorMode();

  const [systemPrefersDark, setSystemPrefersDark] = React.useState(
    () => window.matchMedia("(prefers-color-scheme: dark)").matches,
  );

  React.useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => setSystemPrefersDark(e.matches);
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, []);

  const isDark =
    colorMode === "dark" || (colorMode === "auto" && systemPrefersDark);
  return isDark ? "vs-dark" : "vs-light";
}

export function DocumentPanel({ layout, state }: DocumentPanelProps) {
  const sidebar = useWorkspaceSidebar();
  const selectPanel = useSelectPanel();
  const workspaceFiles = useWorkspaceFiles();
  const isWorkspaceLoading = useEphemeralWorkspaceStateLoading();
  const monacoTheme = useMonacoTheme();

  const fileId = state.data?.fileId ?? null;
  const fileName = state.data?.fileName ?? "";

  // Look up the FileRecord from workspace files so we can get proper signed URL params
  const fileRecord = React.useMemo(
    () => workspaceFiles.find((f) => f.file_id === fileId),
    [workspaceFiles, fileId],
  );

  const {
    data: signedUrl,
    isLoading: isUrlLoading,
    error: signedUrlError,
  } = useSignedUrl(fileRecord);

  const isTextFile = isStructuredText(fileName);
  const isIframeFile = isWebRenderable(fileName);

  // Only restrict file size for the Monaco path — iframes stream content
  // natively and don't load the full file into JavaScript memory.
  const fileTooLarge =
    isTextFile &&
    fileRecord !== undefined &&
    fileRecord.size > MAX_FILE_SIZE_PREVIEW;

  // Fetch text content for structured text files (follows TextContent.tsx pattern).
  // Key on fileId (not signedUrl) so content isn't re-fetched when the signed URL
  // rotates — the underlying file hasn't changed. The queryFn closure captures
  // the current signedUrl, so refetches triggered by staleness use the latest URL.
  // If the fetch fails (e.g. CORS), the component falls back to iframe rendering.
  const contentsQuery = useQuery({
    queryKey: ["documentPanelContents", fileId],
    queryFn: async ({ signal }) => {
      if (!signedUrl) {
        throw new Error("No signed URL available");
      }
      const response = await fetch(signedUrl.toString(), { signal });
      if (!response.ok) {
        throw new Error(
          `Failed to load file contents (HTTP ${response.status.toString()})`,
        );
      }
      return response.text();
    },
    enabled: signedUrl !== undefined && isTextFile && !fileTooLarge,
    // CORS failures are deterministic — retrying won't help
    retry: false,
  });

  const isLoading = isUrlLoading || (isTextFile && contentsQuery.isLoading);

  React.useEffect(() => {
    if (signedUrlError) {
      ErrorMonitoringService.captureError(signedUrlError, {
        panelId: state.id,
        fileId,
        fileName,
      });
    }
  }, [signedUrlError, state.id, fileId, fileName]);

  const openSettings = React.useCallback(() => {
    selectPanel(state.id);
    sidebar.openView({
      type: SidebarViewType.PanelSettings,
      data: undefined,
    });
  }, [selectPanel, sidebar, state.id]);

  if (!state.data) {
    return (
      <PanelLayout layout={layout} state={state}>
        <Alert className={styles["no-file-message"]} severity="info">
          No document selected.{" "}
          <Link component="button" onClick={openSettings} underline="hover">
            Open settings
          </Link>{" "}
          to choose a file.
        </Alert>
      </PanelLayout>
    );
  }

  // File selected but not found in workspace files (may have been removed)
  if (!fileRecord && !isUrlLoading && !isWorkspaceLoading) {
    return (
      <PanelLayout layout={layout} state={state}>
        <Alert className={styles["no-file-message"]} severity="warning">
          File not found in this workspace.{" "}
          <Link component="button" onClick={openSettings} underline="hover">
            Open settings
          </Link>{" "}
          to choose a different file or add files to the workspace.
        </Alert>
      </PanelLayout>
    );
  }

  // For structured text: use Monaco if fetch succeeded, fall back to iframe if it failed (e.g. CORS)
  const useMonaco = isTextFile && contentsQuery.data !== undefined;
  const useIframe =
    (isIframeFile || (isTextFile && contentsQuery.isError)) && !useMonaco;

  return (
    <PanelLayout isLoading={isLoading} layout={layout} state={state}>
      <div className={styles.container}>
        {signedUrlError && (
          <Alert severity="error">
            Failed to load document. Please contact{" "}
            <Link href="mailto:support@roboto.ai" underline="hover">
              support@roboto.ai
            </Link>{" "}
            if this problem persists.
          </Alert>
        )}

        {fileTooLarge && (
          <Alert severity="warning">
            This file exceeds the 50 MB preview limit and cannot be displayed.
          </Alert>
        )}

        {signedUrl && !fileTooLarge && useIframe && (
          <IframeContent
            signedUrl={signedUrl}
            sandbox={
              isHtml(fileName)
                ? "allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                : undefined
            }
          />
        )}

        {!fileTooLarge && useMonaco && (
          <div className={styles.editor}>
            <Editor
              height="100%"
              language={getMonacoLanguage(fileName)}
              theme={monacoTheme}
              value={contentsQuery.data}
              options={{
                readOnly: true,
                lineNumbers: "on",
                scrollBeyondLastLine: false,
                lineNumbersMinChars: 2,
                padding: { top: 8, bottom: 8 },
                contextmenu: false,
                minimap: { enabled: false },
                folding: true,
                automaticLayout: true,
                wordWrap: "on",
                renderLineHighlight: "none",
                overviewRulerBorder: false,
                overviewRulerLanes: 0,
              }}
            />
          </div>
        )}
      </div>
    </PanelLayout>
  );
}
